import { normalizeUrlPolicy, type UrlPolicy } from './url-policy';
import {
  consentUiText,
  isRtlConsentLocale,
  normalizeConsentUiLocale,
  resolveConsentUiLocale,
  type ConsentUiLocale,
  type ConsentUiText,
} from './consent-i18n';
import {
  CONSENT_STYLES,
  renderBannerMarkup,
  renderLauncherMarkup,
  renderPreferencesMarkup,
  renderProviderEntries,
  type ConsentSheetCategory,
} from './consent-view';
import {
  buildProviderModels,
  filterProviders,
  pageProviders,
  type ProviderModel,
} from './consent-providers';

type ConsentCategoryKey = 'necessary' | 'functional' | 'analytics' | 'marketing' | 'support';
type ConsentServiceKey = 'perspectify_recorder' | 'perspectify_feedback_widget';
type OptionalConsentCategoryKey = Exclude<ConsentCategoryKey, 'necessary'>;
type ConsentButtonPosition =
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left'
  | 'middle-right'
  | 'middle-left';

/**
 * The services this SDK can actually start and stop. Anything else in the
 * policy is inventory recorded by the consent scanner: third-party tags on the
 * host page that Perspectify does not load and cannot block. Listing those next
 * to a category toggle would promise the visitor control that does not exist,
 * so they are never rendered.
 */
const ENFORCEABLE_SERVICE_KEYS: readonly string[] = [
  'perspectify_recorder',
  'perspectify_feedback_widget',
];

export function isEnforceableRuntimeService(key: string): boolean {
  return ENFORCEABLE_SERVICE_KEYS.includes(key);
}

interface ConsentCategoryDefinition {
  key: ConsentCategoryKey;
  label: string;
  description: string;
  required: boolean;
}

interface ConsentServiceDefinition {
  key: string;
  label: string;
  description: string;
  effectiveCategory: OptionalConsentCategoryKey | null;
  enabled: boolean;
  required: boolean;
}

/**
 * A reviewed third-party provider the site operator published for visitors to read. The dialog
 * lists it once, and its details show every purpose under that purpose's category. Informational:
 * this SDK does not load or block it, and a site's loaders keep checking the category themselves.
 */
export interface ConsentProviderDefinition {
  /** Stable public id. */
  id: string;
  name: string;
  privacyPolicyUrl: string | null;
  /** One per category, in category order. */
  purposes: Array<{ category: ConsentCategoryKey; text: string }>;
}

const PROVIDER_CATEGORY_KEYS: readonly ConsentCategoryKey[] = ['necessary', 'functional', 'analytics', 'marketing', 'support'];
const PROVIDER_ID = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

/**
 * The providers of a site-config response, read defensively: an entry that is not a well-formed
 * provider is left out rather than rendered, and a privacy link is kept only as an https URL.
 * An older response without `providers` reads as none.
 */
/** Same bound as the server's `MAX_PUBLISHED_PROVIDERS`, which refuses to publish more. */
export const MAX_CONSENT_PROVIDERS = 200;

export function sanitizeConsentProviders(value: unknown): ConsentProviderDefinition[] {
  if (!Array.isArray(value)) return [];
  if (value.length > MAX_CONSENT_PROVIDERS) {
    // The server does not publish more than this; say so rather than drop providers unnoticed.
    console.warn(
      `[Perspectify Browser] The consent policy lists ${value.length} providers; only the first ${MAX_CONSENT_PROVIDERS} are shown.`
    );
  }
  const seen = new Set<string>();
  const providers: ConsentProviderDefinition[] = [];
  for (const entry of value.slice(0, MAX_CONSENT_PROVIDERS)) {
    if (!entry || typeof entry !== 'object') continue;
    const candidate = entry as Record<string, unknown>;
    const id = typeof candidate.id === 'string' ? candidate.id : '';
    const name = typeof candidate.name === 'string' ? candidate.name.trim() : '';
    const purposes: ConsentProviderDefinition['purposes'] = [];
    for (const entry of Array.isArray(candidate.purposes) ? candidate.purposes : []) {
      const category = (entry as { category?: unknown } | null)?.category as ConsentCategoryKey;
      const text = typeof (entry as { text?: unknown } | null)?.text === 'string' ? (entry as { text: string }).text.trim() : '';
      if (!PROVIDER_CATEGORY_KEYS.includes(category) || !text || purposes.some((purpose) => purpose.category === category)) continue;
      purposes.push({ category, text: text.slice(0, 500) });
    }
    if (!PROVIDER_ID.test(id) || seen.has(id) || !name || purposes.length === 0) {
      continue;
    }
    let privacyPolicyUrl: string | null = null;
    if (typeof candidate.privacyPolicyUrl === 'string') {
      try {
        const url = new URL(candidate.privacyPolicyUrl);
        privacyPolicyUrl = url.protocol === 'https:' ? url.toString() : null;
      } catch {
        privacyPolicyUrl = null;
      }
    }
    seen.add(id);
    purposes.sort((left, right) => PROVIDER_CATEGORY_KEYS.indexOf(left.category) - PROVIDER_CATEGORY_KEYS.indexOf(right.category));
    providers.push({ id, name: name.slice(0, 80), privacyPolicyUrl, purposes });
  }
  return providers;
}

export interface RuntimeConsentPolicy {
  projectId: string;
  organizationId: string;
  enabled: boolean;
  version: number;
  bannerTitle: string;
  bannerBody: string;
  preferencesTitle: string;
  manageButtonLabel: string;
  privacyPolicyUrl: string | null;
  legalName: string | null;
  controllerName: string | null;
  controllerEmail: string | null;
  controllerAddress: string | null;
  respectDnt: boolean;
  consentTtlDays: number;
  manageButtonPosition: ConsentButtonPosition;
  categories: ConsentCategoryDefinition[];
  services: ConsentServiceDefinition[];
  /** Published providers, by category. Absent in responses from before providers existed. */
  providers?: ConsentProviderDefinition[];
}

export interface PersistedConsentState {
  version: number;
  updatedAt: number;
  categories: Record<ConsentCategoryKey, boolean>;
}

/** Consent-only hosts must never continue with a missing or malformed public policy. */
export function isValidRuntimeConsentPolicy(value: unknown, projectId?: string): value is RuntimeConsentPolicy {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const policy = value as Record<string, unknown>;
  if (policy.enabled !== true || typeof policy.projectId !== 'string' || !policy.projectId.trim()
    || (projectId !== undefined && policy.projectId !== projectId)
    || typeof policy.organizationId !== 'string' || !policy.organizationId.trim()
    || !Number.isSafeInteger(policy.version) || (policy.version as number) < 1
    || typeof policy.consentTtlDays !== 'number' || !Number.isFinite(policy.consentTtlDays)
    || policy.consentTtlDays < 1 || !Number.isSafeInteger(policy.consentTtlDays * 86400000)
    || typeof policy.respectDnt !== 'boolean') return false;
  for (const key of ['bannerTitle', 'bannerBody', 'preferencesTitle', 'manageButtonLabel']) {
    if (typeof policy[key] !== 'string') return false;
  }
  for (const key of ['privacyPolicyUrl', 'legalName', 'controllerName', 'controllerEmail', 'controllerAddress']) {
    if (policy[key] !== null && typeof policy[key] !== 'string') return false;
  }
  if (!['bottom-right', 'bottom-left', 'top-right', 'top-left', 'middle-right', 'middle-left'].includes(policy.manageButtonPosition as string)
    || !Array.isArray(policy.categories) || !Array.isArray(policy.services)) return false;
  const categories = policy.categories as Array<Partial<ConsentCategoryDefinition> | null>;
  if (categories.length !== PROVIDER_CATEGORY_KEYS.length || !PROVIDER_CATEGORY_KEYS.every((key) =>
    categories.filter((category) => category?.key === key).length === 1
  ) || categories.some((category) => !category || typeof category.label !== 'string'
    || typeof category.description !== 'string' || typeof category.required !== 'boolean'
    || (category.key === 'necessary' && category.required !== true))) return false;
  return policy.services.every((value) => {
    if (!value || typeof value !== 'object') return false;
    const service = value as Partial<ConsentServiceDefinition>;
    return typeof service.key === 'string' && typeof service.label === 'string'
      && typeof service.description === 'string' && typeof service.enabled === 'boolean'
      && typeof service.required === 'boolean' && (service.effectiveCategory === null
        || PROVIDER_CATEGORY_KEYS.filter((key) => key !== 'necessary').includes(service.effectiveCategory as OptionalConsentCategoryKey));
  });
}

type ConsentChangeListener = (state: PersistedConsentState | null) => void;

export interface ConsentEventPayload {
  projectId: string;
  siteOrigin: string;
  path: string | null;
  source: string;
  state: PersistedConsentState;
}

/**
 * Consent records must be attributed to the project whose policy the visitor
 * actually saw, so the project id is always derived from the policy rather than
 * from the page. Letting the server resolve it from the site origin alone drops
 * the record whenever a project serves more than one domain.
 */
export function buildConsentEventPayload(input: {
  policy: Pick<RuntimeConsentPolicy, 'projectId'>;
  siteOrigin: string;
  path: string | null;
  source: string;
  state: PersistedConsentState;
}): ConsentEventPayload {
  return {
    projectId: input.policy.projectId,
    siteOrigin: input.siteOrigin,
    path: input.path,
    source: input.source,
    state: input.state,
  };
}

interface ConsentControllerOptions {
  urlPolicy?: UrlPolicy;
  showManageButton?: boolean;
  showBanner?: boolean;
  autoConsent?: boolean;
  styleNonce?: string;
  /** Language candidates for SDK-owned labels, most preferred first. English when none match. */
  localeCandidates?: ReadonlyArray<string | null | undefined>;
  /** Where the feedback widget's floating trigger sits, so the launcher can step clear of it. */
  feedbackTriggerPosition?: ConsentButtonPosition | null;
}

function ensureConsentStyles(nonce?: string): void {
  const existing = document.getElementById('perspectify-consent-styles') as HTMLStyleElement | null;
  if (existing) {
    if (nonce && !existing.nonce) {
      existing.nonce = nonce;
    }
    return;
  }

  const style = document.createElement('style');
  style.id = 'perspectify-consent-styles';
  if (nonce) {
    style.nonce = nonce;
  }
  style.textContent = CONSENT_STYLES;
  document.head.appendChild(style);
}

function storageKeyForProject(projectId: string): string {
  return `perspectify_consent_${projectId}`;
}

function defaultCategories(
  policy: RuntimeConsentPolicy
): Record<ConsentCategoryKey, boolean> {
  const result: Record<ConsentCategoryKey, boolean> = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
    support: false,
  };

  for (const category of policy.categories) {
    if (category.required) {
      result[category.key] = true;
    }
  }

  return result;
}

/**
 * Validate a stored consent state against the current policy.
 *
 * Exported so the rollout tests can pin the fail-closed rules directly: a
 * policy version bump, an expired `consentTtlDays`, and a corrupt or unknown
 * stored value must all return null, which makes the banner appear again and
 * leaves every optional service stopped.
 */
export function sanitizeStoredConsentState(
  policy: RuntimeConsentPolicy,
  value: unknown
): PersistedConsentState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const stored = value as Record<string, unknown>;
  if (stored.version !== policy.version) {
    return null;
  }

  const now = Date.now();
  if (
    typeof stored.updatedAt !== 'number' ||
    !Number.isFinite(stored.updatedAt) ||
    stored.updatedAt <= 0 ||
    stored.updatedAt > now
  ) {
    return null;
  }

  const maxAgeMs = Math.max(1, policy.consentTtlDays) * 24 * 60 * 60 * 1000;
  if (now - stored.updatedAt >= maxAgeMs) {
    return null;
  }

  if (!stored.categories || typeof stored.categories !== 'object' || Array.isArray(stored.categories)) {
    return null;
  }

  const storedCategories = stored.categories as Record<string, unknown>;
  const categories = defaultCategories(policy);
  for (const category of policy.categories) {
    const selected = Object.prototype.hasOwnProperty.call(storedCategories, category.key)
      ? storedCategories[category.key]
      : undefined;
    // Missing optional categories remain denied; truthy malformed values are
    // never a visitor decision. Re-prompt instead of reviving a corrupt state.
    if (selected !== undefined && typeof selected !== 'boolean') return null;
    if (category.required) {
      categories[category.key] = true;
      continue;
    }

    categories[category.key] = selected === true;
  }

  return {
    version: policy.version,
    updatedAt: stored.updatedAt,
    categories,
  };
}

/** Display-only state the dialog keeps across a same-policy language refresh. */
interface PreferencesViewState {
  checked: Partial<Record<ConsentCategoryKey, boolean>>;
  /** Open disclosures: `about` and/or `legal`. */
  expanded: Set<string>;
  query: string;
  page: number;
  openProviderId: string | null;
  focusSelector: string | null;
  scrollTop: number;
  listScrollTop: number;
}

/** The provider list's browsing state while the dialog is open. Never part of consent. */
interface ProviderBrowseState {
  models: ProviderModel[];
  query: string;
  page: number;
  openProviderId: string | null;
}

/** How long typing has to pause before the result count is announced. */
const SEARCH_ANNOUNCE_DELAY_MS = 700;

let controllerSequence = 0;

/**
 * Shows as much of a just-opened panel as fits, then makes sure the control that opened it is
 * still in view: a panel taller than its scroll region would otherwise scroll the focused
 * control out of sight (WCAG 2.4.11). The panel then starts right below the visible control.
 */
function revealBelow(control: HTMLElement, panel: HTMLElement): void {
  panel.scrollIntoView?.({ block: 'nearest' });
  control.scrollIntoView?.({ block: 'nearest' });
}

/** The attribute that identifies a control across a re-render, so focus can return to it. */
function focusSelectorFor(element: Element | null): string | null {
  if (!element || typeof (element as HTMLElement).getAttribute !== 'function') return null;
  for (const attribute of ['data-action', 'data-key', 'data-disclosure', 'data-provider-row', 'data-role']) {
    const value = element.getAttribute(attribute);
    if (value) return `[${attribute}="${value}"]`;
  }
  if (element.classList?.contains('perspectify-consent-modal')) return '.perspectify-consent-modal';
  return null;
}

export class ConsentController {
  private policy: RuntimeConsentPolicy;
  private readonly siteOrigin: string;
  private readonly apiBaseUrl: string;
  private readonly listeners = new Set<ConsentChangeListener>();
  private readonly storageKey: string;
  private readonly options: Required<Omit<ConsentControllerOptions, 'localeCandidates' | 'feedbackTriggerPosition'>>;
  private readonly feedbackTriggerPosition: ConsentButtonPosition | null;
  private readonly idPrefix: string;
  private locale: ConsentUiLocale;
  private text: ConsentUiText;
  private bannerRoot: HTMLDivElement | null = null;
  private overlayRoot: HTMLDivElement | null = null;
  private manageButton: HTMLButtonElement | null = null;
  private state: PersistedConsentState | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | undefined;
  /** Where focus came from, so closing the dialog can put it back. */
  private focusBeforePreferences: HTMLElement | null = null;
  /** Page elements made inert behind the dialog, with the inert state they had before. */
  private inertedElements: Array<{ element: HTMLElement; wasInert: boolean }> = [];
  private tooltipEscapeListener: ((event: KeyboardEvent) => void) | null = null;
  private providerBrowse: ProviderBrowseState | null = null;
  private announceTimer: ReturnType<typeof setTimeout> | undefined;
  /** Undoes the dialog's fit to the visual viewport (mobile keyboards). */
  private releaseViewportFit: (() => void) | null = null;

  constructor(
    policy: RuntimeConsentPolicy,
    apiBaseUrl: string,
    siteOrigin: string,
    options: ConsentControllerOptions = {}
  ) {
    this.policy = policy;
    this.apiBaseUrl = apiBaseUrl;
    this.siteOrigin = siteOrigin;
    this.storageKey = storageKeyForProject(policy.projectId);
    this.options = {
      urlPolicy: normalizeUrlPolicy(options.urlPolicy),
      showManageButton: options.showManageButton !== false,
      showBanner: options.showBanner !== false,
      autoConsent: options.autoConsent === true,
      styleNonce: options.styleNonce?.trim() ?? '',
    };
    this.feedbackTriggerPosition = options.feedbackTriggerPosition ?? null;
    this.idPrefix = `perspectify-consent-${++controllerSequence}`;
    this.locale = resolveConsentUiLocale(options.localeCandidates ?? []);
    this.text = consentUiText(this.locale);
  }

  init(): PersistedConsentState | null {
    ensureConsentStyles(this.options.styleNonce || undefined);
    this.state = this.loadState();
    window.addEventListener('storage', this.handleStorage);

    if (!this.state && this.policy.respectDnt && navigator.doNotTrack === '1') {
      this.state = this.buildRejectAllState();
      this.persistState(this.state, 'dnt');
    }

    if (!this.state && this.options.autoConsent) {
      this.persistState(this.buildAcceptAllState(), 'auto_accept');
    }

    if (this.options.showManageButton) {
      this.mountManageButton();
    }
    if (!this.state && this.options.showBanner) {
      this.mountBanner();
    }

    this.scheduleExpiry();

    return this.state;
  }

  destroy(): void {
    clearTimeout(this.expiryTimer);
    window.removeEventListener('storage', this.handleStorage);
    this.releaseBackground();
    this.releaseSheet();
    if (this.tooltipEscapeListener) {
      document.removeEventListener('keydown', this.tooltipEscapeListener);
      this.tooltipEscapeListener = null;
    }
    this.bannerRoot?.remove();
    this.overlayRoot?.remove();
    this.manageButton?.remove();
    this.bannerRoot = null;
    this.overlayRoot = null;
    this.manageButton = null;
    this.focusBeforePreferences = null;
    this.listeners.clear();
  }

  onChange(listener: ConsentChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): PersistedConsentState | null {
    this.expireStateIfNeeded();
    return this.state;
  }

  /** The language of the SDK-owned labels currently shown. */
  getLocale(): ConsentUiLocale {
    return this.locale;
  }

  canUseCategory(key: ConsentCategoryKey): boolean {
    this.expireStateIfNeeded();
    if (key === 'necessary') {
      return true;
    }

    return Boolean(this.state?.categories[key]);
  }

  /** The published providers, as the visitor sees them. A copy; changing it changes nothing. */
  getProviders(): ConsentProviderDefinition[] {
    return sanitizeConsentProviders(this.policy.providers).map((provider) => ({
      ...provider,
      purposes: provider.purposes.map((purpose) => ({ ...purpose })),
    }));
  }

  canUseService(key: ConsentServiceKey): boolean {
    const service = this.policy.services.find((entry) => entry.key === key);
    if (!service || !service.enabled) {
      return false;
    }

    if (service.required) {
      return true;
    }

    if (!service.effectiveCategory) {
      return false;
    }

    return this.canUseCategory(service.effectiveCategory);
  }

  openPreferences = (): void => {
    this.mountPreferencesModal();
  };

  /**
   * Swap in the display copy of the same policy fetched for another language. Only text changes:
   * the category set, required flags, service gates, version and stored decision stay exactly as
   * they are, nothing is persisted, and no consent event is sent. A response for another project
   * or another policy version is not this policy's translation, so it is refused (returns false)
   * and the existing re-consent rules apply on the next load as before.
   */
  updateLocalizedCopy(
    next: RuntimeConsentPolicy,
    localeCandidates: ReadonlyArray<string | null | undefined>
  ): boolean {
    if (next.projectId !== this.policy.projectId || next.version !== this.policy.version) {
      return false;
    }

    const labelFor = <T extends { key: string; label: string; description: string }>(
      entries: readonly T[] | undefined,
      key: string
    ) => entries?.find((entry) => entry && entry.key === key);
    const text = (value: unknown, fallback: string) => (typeof value === 'string' ? value : fallback);
    const optionalText = (value: unknown, fallback: string | null) =>
      value === null || typeof value === 'string' ? value : fallback;

    this.policy = {
      ...this.policy,
      bannerTitle: text(next.bannerTitle, this.policy.bannerTitle),
      bannerBody: text(next.bannerBody, this.policy.bannerBody),
      preferencesTitle: text(next.preferencesTitle, this.policy.preferencesTitle),
      manageButtonLabel: text(next.manageButtonLabel, this.policy.manageButtonLabel),
      privacyPolicyUrl: optionalText(next.privacyPolicyUrl, this.policy.privacyPolicyUrl),
      legalName: optionalText(next.legalName, this.policy.legalName),
      controllerName: optionalText(next.controllerName, this.policy.controllerName),
      controllerEmail: optionalText(next.controllerEmail, this.policy.controllerEmail),
      controllerAddress: optionalText(next.controllerAddress, this.policy.controllerAddress),
      categories: this.policy.categories.map((category) => {
        const translated = labelFor(next.categories, category.key);
        return translated
          ? {
              ...category,
              label: text(translated.label, category.label),
              description: text(translated.description, category.description),
            }
          : category;
      }),
      services: this.policy.services.map((service) => {
        const translated = labelFor(next.services, service.key);
        return translated
          ? {
              ...service,
              label: text(translated.label, service.label),
              description: text(translated.description, service.description),
            }
          : service;
      }),
      providers: sanitizeConsentProviders(next.providers),
    };
    this.locale = resolveConsentUiLocale(localeCandidates);
    this.text = consentUiText(this.locale);

    if (this.manageButton) {
      this.renderManageButton(this.manageButton);
    }
    if (this.bannerRoot) {
      const focus = this.bannerRoot.contains(document.activeElement)
        ? focusSelectorFor(document.activeElement)
        : null;
      this.renderBanner(this.bannerRoot);
      if (focus) this.bannerRoot.querySelector<HTMLElement>(focus)?.focus();
    }
    if (this.overlayRoot) {
      this.renderPreferences(this.overlayRoot, this.capturePreferencesViewState(this.overlayRoot));
    }
    return true;
  }

  private buildRejectAllState(): PersistedConsentState {
    return {
      version: this.policy.version,
      updatedAt: Date.now(),
      categories: defaultCategories(this.policy),
    };
  }

  private buildAcceptAllState(): PersistedConsentState {
    const categories = defaultCategories(this.policy);
    for (const category of this.policy.categories) {
      categories[category.key] = true;
    }

    return {
      version: this.policy.version,
      updatedAt: Date.now(),
      categories,
    };
  }

  private loadState(): PersistedConsentState | null {
    const candidates: PersistedConsentState[] = [];
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      const state = this.parseStoredState(raw);
      if (state) candidates.push(state);
    } catch {
      // Cookie persistence remains available when Web Storage is restricted.
    }
    try {
      const prefix = `${this.storageKey}=`;
      for (const cookie of document.cookie.split(';')) {
        if (!cookie.trim().startsWith(prefix)) continue;
        const state = this.parseStoredState(decodeURIComponent(cookie.trim().slice(prefix.length)));
        if (state) candidates.push(state);
      }
    } catch {
      // A malformed cookie must not invalidate a valid localStorage decision.
    }
    return candidates.reduce<PersistedConsentState | null>((latest, candidate) => {
      if (!latest || candidate.updatedAt > latest.updatedAt) return candidate;
      if (candidate.updatedAt < latest.updatedAt) return latest;
      const categories = { ...latest.categories };
      for (const category of this.policy.categories) {
        if (!category.required) categories[category.key] &&= candidate.categories[category.key];
      }
      return { ...latest, categories };
    }, null);
  }

  private parseStoredState(raw: string | null): PersistedConsentState | null {
    try {
      return raw ? sanitizeStoredConsentState(this.policy, JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  private handleStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== this.storageKey) return;
    try {
      if (event.storageArea !== null && event.storageArea !== window.localStorage) return;
    } catch {
      return;
    }
    const state = event.key === null ? null : this.parseStoredState(event.newValue);
    if (!state) {
      // An explicit clear or invalidation in another tab must not resurrect an old cookie.
      try { document.cookie = `${this.storageKey}=; Max-Age=0; Path=/; SameSite=Lax`; } catch { /* Restricted cookies. */ }
    }
    this.applyExternalState(state);
  };

  private applyExternalState(state: PersistedConsentState | null): void {
    this.state = state;
    if (state) {
      this.bannerRoot?.remove();
      this.bannerRoot = null;
    } else if (this.options.showBanner) {
      this.mountBanner();
    }
    this.closePreferences();
    this.syncManageButton();
    this.scheduleExpiry();
    this.emit();
  }

  private expireStateIfNeeded(): void {
    if (this.state && Date.now() - this.state.updatedAt >= Math.max(1, this.policy.consentTtlDays) * 86400000) {
      this.applyExternalState(null);
    }
  }

  private scheduleExpiry(): void {
    clearTimeout(this.expiryTimer);
    this.expiryTimer = undefined;
    if (!this.state) return;
    const remaining = this.state.updatedAt + Math.max(1, this.policy.consentTtlDays) * 86400000 - Date.now();
    if (remaining <= 0) {
      this.applyExternalState(null);
      return;
    }
    // Browser timers cap near 24 days, so long lifetimes need another check.
    this.expiryTimer = setTimeout(() => {
      this.expireStateIfNeeded();
      this.scheduleExpiry();
    }, Math.min(remaining, 2147483647));
  }

  private persistState(state: PersistedConsentState, source: string): void {
    this.state = state;

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch {
      // A failed withdrawal write must not leave a surviving localStorage grant.
      try { window.localStorage.removeItem(this.storageKey); } catch { /* Restricted storage. */ }
    }

    try {
      const encoded = encodeURIComponent(JSON.stringify(state));
      const maxAge = Math.max(1, this.policy.consentTtlDays) * 24 * 60 * 60;
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${this.storageKey}=${encoded}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
    } catch {
      // cookie write failure is non-fatal.
    }

    this.recordEvent(source, state);
    this.bannerRoot?.remove();
    this.bannerRoot = null;
    this.syncManageButton();
    // Deciding from inside the dialog closes it, so focus has to be handed back
    // the same way an explicit close does.
    this.closePreferences();
    this.scheduleExpiry();
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  private async recordEvent(source: string, state: PersistedConsentState): Promise<void> {
    try {
      await fetch(`${this.apiBaseUrl}/api/public/consent/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        ...(this.options.urlPolicy === 'origin-only' ? { referrerPolicy: 'origin' as const } : {}),
        body: JSON.stringify(
          buildConsentEventPayload({
            policy: this.policy,
            siteOrigin: this.siteOrigin,
            path: this.options.urlPolicy === 'origin-only' ? null : window.location.pathname,
            source,
            state,
          })
        ),
      });
    } catch {
      // event capture is best-effort only
    }
  }

  /**
   * Declares the language of the SDK labels (the page's own tag when it is the same language,
   * which also lets long words hyphenate), and lays Arabic out right to left. A page that
   * declares its own direction already passes it down (`all: initial` does not reset
   * `direction`), so it is left alone.
   */
  private applyDirection(element: HTMLElement): void {
    const pageLang = document.documentElement?.lang ?? '';
    element.lang = normalizeConsentUiLocale(pageLang) === this.locale ? pageLang : this.locale;
    const pageDeclaresDirection = Boolean(document.documentElement?.dir || document.body?.dir);
    element.dir = !pageDeclaresDirection && isRtlConsentLocale(this.locale) ? 'rtl' : '';
  }

  /** The banner already offers every choice, so the floating button waits until it is gone. */
  private syncManageButton(): void {
    if (this.manageButton) {
      this.manageButton.hidden = this.bannerRoot !== null;
    }
  }

  private mountManageButton(): void {
    if (this.manageButton) {
      return;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'perspectify-consent-root perspectify-consent-manage';
    this.applyManageButtonPosition(button);
    this.renderManageButton(button);
    button.addEventListener('click', () => this.openPreferences());
    // A shown tooltip can be dismissed with Escape without moving the pointer or focus, wherever
    // focus is (WCAG 1.4.13). The key is only observed: the dialog's own Escape handling and
    // the host page's keep working, so nothing is prevented or stopped here.
    this.tooltipEscapeListener = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || this.overlayRoot) return;
      if (button.matches(':hover') || button.matches(':focus-visible')) {
        button.setAttribute('data-tooltip-dismissed', '');
      }
    };
    document.addEventListener('keydown', this.tooltipEscapeListener);
    // Dismissal lasts until both hover and focus have ended.
    button.addEventListener('pointerleave', () => {
      if (!button.matches(':focus-visible')) button.removeAttribute('data-tooltip-dismissed');
    });
    button.addEventListener('blur', () => {
      if (!button.matches(':hover')) button.removeAttribute('data-tooltip-dismissed');
    });
    document.body.appendChild(button);
    this.manageButton = button;
    this.syncManageButton();
  }

  /** Icon launcher: the configured label is its accessible name and its tooltip. */
  private renderManageButton(button: HTMLButtonElement): void {
    button.setAttribute('aria-label', this.policy.manageButtonLabel);
    button.innerHTML = renderLauncherMarkup(this.policy.manageButtonLabel);
    this.applyDirection(button);
  }

  private mountBanner(): void {
    if (this.bannerRoot) {
      return;
    }

    const root = document.createElement('div');
    root.className = 'perspectify-consent-root perspectify-consent-banner';
    // The banner is not modal — the page stays usable behind it — so it is a
    // labelled region rather than a dialog. Without a role and a name it
    // reaches assistive technology as an unannounced anonymous div.
    root.setAttribute('role', 'region');
    this.renderBanner(root);
    document.body.appendChild(root);
    this.bannerRoot = root;
    this.syncManageButton();
  }

  private renderBanner(root: HTMLDivElement): void {
    root.setAttribute('aria-label', this.policy.bannerTitle);
    this.applyDirection(root);
    root.innerHTML = renderBannerMarkup({
      idPrefix: this.idPrefix,
      title: this.policy.bannerTitle,
      body: this.policy.bannerBody,
      privacyPolicyUrl: this.policy.privacyPolicyUrl,
      text: this.text,
    });

    root.querySelector('[data-action="accept"]')?.addEventListener('click', () => {
      this.persistState(this.buildAcceptAllState(), 'accept_all');
    });
    root.querySelector('[data-action="reject"]')?.addEventListener('click', () => {
      this.persistState(this.buildRejectAllState(), 'reject_all');
    });
    root.querySelector('[data-action="preferences"]')?.addEventListener('click', () => {
      this.mountPreferencesModal();
    });
  }

  private mountPreferencesModal(): void {
    // Reopening replaces the dialog rather than stacking one. The opener was
    // recorded when the first one mounted, so it must survive the swap —
    // re-reading it here would capture the dialog that is being discarded.
    const reopening = this.overlayRoot !== null;
    this.overlayRoot?.remove();
    this.overlayRoot = null;

    if (!reopening) {
      this.focusBeforePreferences =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }

    const overlay = document.createElement('div');
    overlay.className = 'perspectify-consent-root perspectify-consent-overlay';
    this.renderPreferences(overlay, null);

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        this.closePreferences();
      }
    });

    // aria-modal only describes the dialog; the page behind is also made inert
    // below. Escape closes without a decision, and Tab wraps inside the dialog.
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePreferences();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = this.focusableWithin(overlay);
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && (active === first || active === overlay.querySelector('.perspectify-consent-modal') || !overlay.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !overlay.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    });

    // The undecided banner steps aside while the dialog is open; closing
    // without a decision brings it back.
    if (this.bannerRoot) {
      this.bannerRoot.hidden = true;
    }
    document.body.appendChild(overlay);
    this.overlayRoot = overlay;
    this.inertBackground(overlay);
    this.fitToVisualViewport(overlay);
    // Land on the dialog itself rather than its first button, so a screen
    // reader announces the dialog's name before any control.
    const modal = overlay.querySelector<HTMLElement>('.perspectify-consent-modal');
    if (modal) {
      modal.tabIndex = -1;
      modal.focus();
    } else {
      this.focusableWithin(overlay)[0]?.focus();
    }
  }

  private buildSheetCategories(viewState: PreferencesViewState | null): ConsentSheetCategory[] {
    const currentState = this.state ?? this.buildRejectAllState();
    return this.policy.categories.map((category) => ({
      key: category.key,
      label: category.label,
      description: category.description,
      required: category.required,
      checked: category.required
        ? true
        : viewState?.checked[category.key] ?? Boolean(currentState.categories[category.key]),
      services: this.getVisibleServicesForCategory(category.key),
    }));
  }

  private buildProviderModels(): ProviderModel[] {
    const labels = Object.fromEntries(this.policy.categories.map((category) => [category.key, category.label]));
    return buildProviderModels(this.getProviders(), labels, PROVIDER_CATEGORY_KEYS, this.locale);
  }

  private legalDetails(): string[] {
    return [
      this.policy.legalName,
      this.policy.controllerName,
      this.policy.controllerEmail,
      this.policy.controllerAddress,
    ].filter((value): value is string => Boolean(value && value.trim()));
  }

  /** Renders (or re-renders) the dialog content inside an overlay, keeping a captured view state. */
  private renderPreferences(overlay: HTMLDivElement, viewState: PreferencesViewState | null): void {
    const models = this.buildProviderModels();
    this.providerBrowse = {
      models,
      query: viewState?.query ?? '',
      page: viewState?.page ?? 0,
      openProviderId: viewState?.openProviderId ?? null,
    };

    this.applyDirection(overlay);
    overlay.innerHTML = renderPreferencesMarkup({
      idPrefix: this.idPrefix,
      title: this.policy.preferencesTitle,
      privacyPolicyUrl: this.policy.privacyPolicyUrl,
      legalDetails: this.legalDetails(),
      categories: this.buildSheetCategories(viewState),
      providerTotal: models.length,
      expanded: viewState?.expanded ?? new Set<string>(),
      text: this.text,
    });

    overlay.querySelector('[data-action="close"]')?.addEventListener('click', () => {
      this.closePreferences();
    });

    overlay.querySelector('[data-action="accept"]')?.addEventListener('click', () => {
      this.persistState(this.buildAcceptAllState(), 'preferences_accept_all');
    });

    overlay.querySelector('[data-action="reject"]')?.addEventListener('click', () => {
      this.persistState(this.buildRejectAllState(), 'preferences_reject_all');
    });

    overlay.querySelector('[data-action="save"]')?.addEventListener('click', () => {
      const nextState = this.buildRejectAllState();
      overlay.querySelectorAll<HTMLInputElement>('.perspectify-consent-toggle[data-key]').forEach((toggle) => {
        const key = toggle.dataset.key as ConsentCategoryKey | undefined;
        if (!key) {
          return;
        }
        nextState.categories[key] = toggle.checked;
      });
      nextState.updatedAt = Date.now();
      this.persistState(nextState, 'preferences_save');
    });

    // A checkbox only changes its own pending value, and nothing in the provider list changes
    // consent at all: searching, paging and opening details neither save nor report anything.
    overlay.querySelectorAll<HTMLButtonElement>('.perspectify-consent-disclosure[aria-controls]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = overlay.querySelector<HTMLElement>(`#${button.getAttribute('aria-controls')}`);
        if (!panel) return;
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = !open;
        if (open) revealBelow(button, panel);
      });
    });

    const search = overlay.querySelector<HTMLInputElement>('[data-role="provider-search"]');
    if (search) {
      search.value = this.providerBrowse.query;
      search.addEventListener('input', () => {
        if (!this.providerBrowse) return;
        this.providerBrowse.query = search.value;
        this.providerBrowse.page = 0;
        this.providerBrowse.openProviderId = null;
        this.renderProviderList(overlay, { announce: 'debounced', resetScroll: true });
      });
      // Escape first clears a search in progress; with the field empty it closes the dialog.
      search.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !search.value) return;
        event.preventDefault();
        event.stopPropagation();
        search.value = '';
        search.dispatchEvent(new Event('input'));
      });
    }

    const changePage = (delta: number, pressed: HTMLButtonElement) => {
      if (!this.providerBrowse) return;
      this.providerBrowse.page += delta;
      this.providerBrowse.openProviderId = null;
      this.renderProviderList(overlay, { announce: 'now', resetScroll: true });
      // A pager button disabled under the visitor's focus would drop focus to the page body.
      if (pressed.disabled) {
        overlay.querySelector<HTMLButtonElement>(delta > 0 ? '[data-action="previous-page"]' : '[data-action="next-page"]')?.focus();
      }
    };
    const previous = overlay.querySelector<HTMLButtonElement>('[data-action="previous-page"]');
    const next = overlay.querySelector<HTMLButtonElement>('[data-action="next-page"]');
    previous?.addEventListener('click', () => changePage(-1, previous));
    next?.addEventListener('click', () => changePage(1, next));

    overlay.querySelector('.perspectify-consent-provider-list')?.addEventListener('click', (event) => {
      const target = event.target as Element | null;
      const row = target?.closest?.<HTMLButtonElement>('[data-provider-row]');
      if (row) {
        this.toggleProviderDetails(overlay, row.dataset.providerRow ?? null);
        return;
      }
      if (target?.closest?.('[data-action="clear-search"]') && search) {
        search.value = '';
        if (this.providerBrowse) {
          this.providerBrowse.query = '';
          this.providerBrowse.page = 0;
          this.providerBrowse.openProviderId = null;
        }
        this.renderProviderList(overlay, { announce: 'now', resetScroll: true });
        search.focus();
      }
    });

    this.renderProviderList(overlay, { announce: 'none', resetScroll: false });

    if (viewState) {
      const body = overlay.querySelector<HTMLElement>('.perspectify-consent-body');
      if (body) body.scrollTop = viewState.scrollTop;
      const list = overlay.querySelector<HTMLElement>('.perspectify-consent-provider-list');
      if (list) list.scrollTop = viewState.listScrollTop;
      const modal = overlay.querySelector<HTMLElement>('.perspectify-consent-modal');
      if (modal) modal.tabIndex = -1;
      const target = viewState.focusSelector
        ? overlay.querySelector<HTMLElement>(viewState.focusSelector)
        : null;
      (target ?? modal)?.focus();
    }
  }

  /**
   * Re-renders only the provider page, range and pager. The search field and the category
   * checkboxes stay untouched, so typing keeps focus and drafts survive every change here.
   */
  private renderProviderList(
    overlay: HTMLDivElement,
    options: { announce: 'none' | 'now' | 'debounced'; resetScroll: boolean }
  ): void {
    const browse = this.providerBrowse;
    const list = overlay.querySelector<HTMLElement>('.perspectify-consent-provider-list');
    if (!browse || !list) return;
    const matches = filterProviders(browse.models, browse.query, this.locale);
    const page = pageProviders(matches, browse.page);
    browse.page = page.page;
    if (browse.openProviderId && !page.items.some((item) => item.id === browse.openProviderId)) {
      browse.openProviderId = null;
    }

    list.innerHTML = renderProviderEntries({
      idPrefix: this.idPrefix,
      items: page.items,
      openId: browse.openProviderId,
      emptyKind: browse.models.length === 0 ? 'none' : page.total === 0 ? 'no-match' : null,
      text: this.text,
    });
    if (options.resetScroll) list.scrollTop = 0;

    const summary =
      page.total === 0
        ? this.text.noMatches
        : page.total === 1
          ? this.text.providerCount(1)
          : this.text.providerRange(page.from, page.to, page.total);
    const range = overlay.querySelector<HTMLElement>('[data-role="provider-range"]');
    if (range) range.textContent = summary;
    const previous = overlay.querySelector<HTMLButtonElement>('[data-action="previous-page"]');
    const next = overlay.querySelector<HTMLButtonElement>('[data-action="next-page"]');
    if (previous) previous.disabled = page.page === 0;
    if (next) next.disabled = page.page >= page.pageCount - 1;

    // Results are announced once typing settles, and at once for a page change; never per key.
    const status = overlay.querySelector<HTMLElement>('[data-role="provider-status"]');
    clearTimeout(this.announceTimer);
    this.announceTimer = undefined;
    if (!status || options.announce === 'none' || browse.models.length === 0) return;
    if (options.announce === 'now') {
      status.textContent = summary;
    } else {
      this.announceTimer = setTimeout(() => {
        status.textContent = summary;
      }, SEARCH_ANNOUNCE_DELAY_MS);
    }
  }

  /** One provider open at a time, toggled in place so focus stays on the row. */
  private toggleProviderDetails(overlay: HTMLDivElement, providerId: string | null): void {
    if (!this.providerBrowse || !providerId) return;
    const opening = this.providerBrowse.openProviderId !== providerId;
    this.providerBrowse.openProviderId = opening ? providerId : null;
    overlay.querySelectorAll<HTMLButtonElement>('[data-provider-row]').forEach((row) => {
      const open = opening && row.dataset.providerRow === providerId;
      row.setAttribute('aria-expanded', open ? 'true' : 'false');
      const detail = overlay.querySelector<HTMLElement>(`#${row.getAttribute('aria-controls')}`);
      if (detail) detail.hidden = !open;
      if (open && detail) revealBelow(row, detail);
    });
  }

  private capturePreferencesViewState(overlay: HTMLDivElement): PreferencesViewState {
    const checked: PreferencesViewState['checked'] = {};
    overlay.querySelectorAll<HTMLInputElement>('.perspectify-consent-toggle[data-key]').forEach((toggle) => {
      const key = toggle.dataset.key as ConsentCategoryKey | undefined;
      if (key) checked[key] = toggle.checked;
    });
    const expanded = new Set<string>();
    overlay.querySelectorAll<HTMLButtonElement>('.perspectify-consent-disclosure[aria-expanded="true"]').forEach((button) => {
      if (button.dataset.disclosure) expanded.add(button.dataset.disclosure);
    });
    const active = document.activeElement;
    return {
      checked,
      expanded,
      query: this.providerBrowse?.query ?? '',
      page: this.providerBrowse?.page ?? 0,
      openProviderId: this.providerBrowse?.openProviderId ?? null,
      focusSelector: active && overlay.contains(active) ? focusSelectorFor(active) : null,
      scrollTop: overlay.querySelector<HTMLElement>('.perspectify-consent-body')?.scrollTop ?? 0,
      listScrollTop: overlay.querySelector<HTMLElement>('.perspectify-consent-provider-list')?.scrollTop ?? 0,
    };
  }

  /**
   * Keeps the dialog inside the visible area when a mobile keyboard shrinks the visual viewport,
   * so the focused search field and the actions are not pushed off screen.
   */
  private fitToVisualViewport(overlay: HTMLDivElement): void {
    this.releaseViewportFit?.();
    if (typeof window === 'undefined') return;
    const viewport = window.visualViewport;
    let frame = 0;
    // A shrinking viewport (a keyboard opening over the focused search field, a rotation) can
    // switch the sheet's layout and leave the focused control outside its scroll region. Once
    // the new layout has applied, bring that control back into view (WCAG 2.4.11).
    const keepFocusVisible = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== overlay && overlay.contains(active)
          && !active.classList.contains('perspectify-consent-modal')) {
          active.scrollIntoView?.({ block: 'nearest' });
        }
      });
    };
    const fit = () => {
      if (viewport) {
        overlay.style.top = `${viewport.offsetTop}px`;
        overlay.style.height = `${viewport.height}px`;
        overlay.style.bottom = 'auto';
      }
      keepFocusVisible();
    };
    if (viewport) {
      fit();
      viewport.addEventListener('resize', fit);
      viewport.addEventListener('scroll', fit);
    }
    window.addEventListener('resize', fit);
    this.releaseViewportFit = () => {
      cancelAnimationFrame(frame);
      viewport?.removeEventListener('resize', fit);
      viewport?.removeEventListener('scroll', fit);
      window.removeEventListener('resize', fit);
      this.releaseViewportFit = null;
    };
  }

  /**
   * Controls a visitor can actually reach: a link inside a collapsed panel is
   * in the DOM but hidden, and must not become a Tab stop.
   */
  private focusableWithin(root: HTMLElement): HTMLElement[] {
    return Array.from(
      root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.closest('[hidden], [inert]') && element.getClientRects().length > 0);
  }

  /** Make everything behind the dialog inert, remembering what was already inert. */
  private inertBackground(overlay: HTMLElement): void {
    this.releaseBackground();
    const children = document.body?.children ? Array.from(document.body.children) : [];
    for (const child of children) {
      if (child === overlay || !(child instanceof HTMLElement)) continue;
      this.inertedElements.push({ element: child, wasInert: child.inert === true });
      child.inert = true;
    }
  }

  private releaseBackground(): void {
    for (const { element, wasInert } of this.inertedElements) {
      element.inert = wasInert;
    }
    this.inertedElements = [];
  }

  private closePreferences(): void {
    if (!this.overlayRoot) {
      return;
    }

    this.overlayRoot.remove();
    this.overlayRoot = null;
    this.releaseBackground();
    this.releaseSheet();
    if (this.bannerRoot) {
      this.bannerRoot.hidden = false;
    }
    // Returning focus to the control that opened the dialog keeps a keyboard
    // visitor where they were instead of resetting them to the top of the page.
    // The opener can be gone: a language refresh re-renders the banner's
    // buttons, and a decision removes the banner. Then the visible control that
    // opens preferences now (the banner's, else the floating one) stands in.
    const restoreTo = this.focusBeforePreferences;
    this.focusBeforePreferences = null;
    if (restoreTo?.isConnected) {
      restoreTo.focus();
    } else if (restoreTo) {
      this.currentPreferencesOpener()?.focus();
    }
  }

  private releaseSheet(): void {
    clearTimeout(this.announceTimer);
    this.announceTimer = undefined;
    this.releaseViewportFit?.();
    this.providerBrowse = null;
  }

  private currentPreferencesOpener(): HTMLElement | null {
    if (this.bannerRoot?.isConnected && !this.bannerRoot.hidden) {
      return this.bannerRoot.querySelector<HTMLElement>('[data-action="preferences"]');
    }
    if (this.manageButton?.isConnected && !this.manageButton.hidden) {
      return this.manageButton;
    }
    return null;
  }

  private applyManageButtonPosition(button: HTMLButtonElement): void {
    const positionClasses = new Set([
      'perspectify-consent-manage--bottom-right',
      'perspectify-consent-manage--bottom-left',
      'perspectify-consent-manage--top-right',
      'perspectify-consent-manage--top-left',
      'perspectify-consent-manage--middle-right',
      'perspectify-consent-manage--middle-left',
    ]);
    const classes = button.className
      .split(/\s+/)
      .filter((entry) => entry && !positionClasses.has(entry));
    const stacked =
      this.feedbackTriggerPosition !== null &&
      this.feedbackTriggerPosition === this.policy.manageButtonPosition;
    const addPositionClass = (value: string) => {
      button.className = [
        ...classes.filter((entry) => entry !== 'perspectify-consent-manage--stacked'),
        value,
        ...(stacked ? ['perspectify-consent-manage--stacked'] : []),
      ].join(' ');
    };

    switch (this.policy.manageButtonPosition) {
      case 'bottom-right':
        addPositionClass('perspectify-consent-manage--bottom-right');
        break;
      case 'top-right':
        addPositionClass('perspectify-consent-manage--top-right');
        break;
      case 'top-left':
        addPositionClass('perspectify-consent-manage--top-left');
        break;
      case 'middle-right':
        addPositionClass('perspectify-consent-manage--middle-right');
        break;
      case 'middle-left':
        addPositionClass('perspectify-consent-manage--middle-left');
        break;
      case 'bottom-left':
      default:
        addPositionClass('perspectify-consent-manage--bottom-left');
        break;
    }
  }

  private getVisibleServices(): ConsentServiceDefinition[] {
    return this.policy.services.filter(
      (service) =>
        isEnforceableRuntimeService(service.key) &&
        service.enabled &&
        (service.required || Boolean(service.effectiveCategory))
    );
  }

  private getVisibleServicesForCategory(categoryKey: ConsentCategoryKey): ConsentServiceDefinition[] {
    return this.getVisibleServices().filter((service) =>
      service.required ? categoryKey === 'necessary' : service.effectiveCategory === categoryKey
    );
  }
}
